/*!
 * dvd-video-author.js — builds a playable DVD-Video VIDEO_TS structure in pure JS.
 *
 * ffmpeg (WASM in the browser, native in the Node test harness) muxes a
 * DVD-compliant MPEG-2 program stream and writes NAV pack SKELETONS at every
 * VOBU boundary — but leaves both payloads (PCI and DSI) entirely zero. A disc
 * like that persuades some players to start, yet chapter jumps, FF/REW and time
 * search are dead, and there is no IFO at all. This module fills the NAV packs
 * and generates the VMG/VTS IFO+BUP set, which is what turns a pile of VOB
 * sectors into a disc a hardware player can navigate.
 *
 * Byte layouts are taken from the two authoritative implementations and were
 * cross-checked against each other AND against a VOB produced locally:
 *   - libdvdread  src/dvdread/nav_types.h + src/nav_read.c   (reader side)
 *   - dvdauthor   src/dvdvob.c FixVobus, src/dvdifo.c, src/dvdpgc.c  (writer side)
 * Both agree: inside a 2048-byte NAV pack the PCI payload starts at 0x2D and the
 * DSI payload at 0x407 (so e.g. vobu_ea lands on 0x40F).
 *
 * Everything here is pure data in / data out so the same code is exercised by
 * the Node harness on real files and by the browser at runtime.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    else root.__DvdAuthor = api;
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var SECTOR = 2048;
    var VOB_MAX_SECTORS = 524288;      // 1 GiB — the DVD limit on a single .VOB file
    var IFO_MIN_SECTORS = 16;          // keeps .IFO and .BUP in different ECC blocks
    var SRI_END_OF_CELL = 0x3fffffff;  // "no such VOBU" marker in the DSI seek tables

    // 19 FF/REW time steps, in half-seconds, exactly as commercial discs use them.
    var TIMELINE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 20, 60, 120, 240];
    var HALFSEC = 45000;               // 0.5 s in 90 kHz ticks

    // ── little helpers ────────────────────────────────────────────────────────
    function be16(u8, o, v) { u8[o] = (v >>> 8) & 255; u8[o + 1] = v & 255; }
    function be32(u8, o, v) {
        u8[o] = (v / 16777216) & 255; u8[o + 1] = (v >>> 16) & 255;
        u8[o + 2] = (v >>> 8) & 255; u8[o + 3] = v & 255;
    }
    function rd16(u8, o) { return (u8[o] << 8) | u8[o + 1]; }

    /* SCR out of a pack header, truncated to 90 kHz (the 27 MHz extension is
       dropped exactly as dvdauthor's readscr does). */
    function readScr(u8, o) {
        return ((u8[o] & 0x38) * 134217728) +   // <<27
               ((u8[o] & 3) * 268435456) +      // <<28
               (u8[o + 1] * 1048576) +
               ((u8[o + 2] & 0xf8) * 4096) +
               ((u8[o + 2] & 3) * 8192) +
               (u8[o + 3] * 32) +
               ((u8[o + 4] & 0xf8) >> 3);
    }

    /* 33-bit PTS/DTS out of a PES header. Multiplication, not <<, because the
       top bits overflow a signed 32-bit shift. */
    function readPts(u8, o) {
        return (((u8[o] >> 1) & 7) * 1073741824) +
               (u8[o + 1] * 4194304) +
               (((u8[o + 2] >> 1) & 127) * 32768) +
               (u8[o + 3] * 128) +
               ((u8[o + 4] >> 1) & 127);
    }

    function tobcd(v) { return ((v / 10) | 0) * 16 + (v % 10); }

    /* Playback-time field: BCD hhmmssff with the frame-rate code in the two top
       bits of the frame byte. Mirrors dvdauthor buildtimehelper(), including its
       half-frame rounding bias. */
    function bcdTime(ticks, pal) {
        var denom = pal ? 90000 : 90090;
        var frate = pal ? 25 : 30;
        var rc = pal ? 1 : 3;
        var num = ticks + ((denom / (frate * 2)) | 0) + 1;
        var sec = Math.floor(num / denom);
        var min = Math.floor(sec / 60);
        var hr = tobcd(Math.floor(min / 60));
        var mi = tobcd(min % 60);
        var se = tobcd(sec % 60);
        num = num % denom;
        var fr = tobcd(Math.floor(num * frate / denom));
        return (hr * 16777216) + (mi * 65536) + (se * 256) + fr + (rc * 64);
    }

    function frameTicks(pal) { return pal ? 3600 : 3003; }   // 25 fps / 29.97 fps

    // ── VOB parsing ───────────────────────────────────────────────────────────
    /* Walks the PES packets inside one 2048-byte pack. `cb(streamId, payloadOff,
       payloadLen, pts)`. Padding (0xBE) and private_stream_2 (0xBF) carry no PES
       extension header; everything else we care about does. */
    function walkPack(u8, base, cb) {
        var stuffing = u8[base + 13] & 7;
        var o = base + 14 + stuffing;
        var limit = base + SECTOR;
        while (o + 6 <= limit) {
            if (u8[o] !== 0 || u8[o + 1] !== 0 || u8[o + 2] !== 1) break;
            var id = u8[o + 3];
            var len = rd16(u8, o + 4);
            if (len <= 0 || o + 6 + len > limit) break;
            var payload = o + 6, plen = len, pts = null;
            if (id !== 0xBE && id !== 0xBF) {
                var flags2 = u8[o + 7], hlen = u8[o + 8];
                if (flags2 & 0x80) pts = readPts(u8, o + 9);
                payload = o + 9 + hlen;
                plen = len - 3 - hlen;
            }
            if (cb(id, payload, plen, pts) === false) return;
            o += 6 + len;
        }
    }

    function isNavPack(u8, o) {
        return u8[o] === 0 && u8[o + 1] === 0 && u8[o + 2] === 1 && u8[o + 3] === 0xBA &&
               u8[o + 0x26] === 0 && u8[o + 0x27] === 0 && u8[o + 0x28] === 1 &&
               u8[o + 0x29] === 0xBF && u8[o + 0x2c] === 0;
    }

    /* Scans one VOBU. Stops as soon as it has what the NAV pack needs (first
       PTS, up to 4 picture boundaries, first audio sector) so cost is a handful
       of sectors per VOBU rather than the whole stream — the difference between
       "instant" and "minutes" on a 2-hour disc.
       `full` forces a complete scan; used on the final VOBU where the picture
       count and the sequence_end_code both matter. */
    function scanVobu(u8, startSect, endSect, full, audioSubs) {
        // audioSubs: the private_stream_1 sub-stream ids the disc carries
        // (0x80 AC-3, 0x88 DTS, 0xA0 LPCM …). For a multi-audio title the DSI
        // needs the first sector of EACH stream, not just the first audio pack.
        var want = audioSubs && audioSubs.length ? audioSubs.length : 1;
        var v = {
            sector: startSect, lastSector: endSect,
            sPts: null, hasVideo: false, pictures: 0,
            picSectors: [],            // sector in which each picture starts
            audioSector: -1, audioSub: -1,
            audioFirst: {},            // sub-stream id -> first sector in this VOBU
            hasSeqEnd: false, lastVideoPts: null
        };
        var carry0 = 0, carry1 = 0, carry2 = 0;
        for (var s = startSect; s <= endSect; s++) {
            var base = s * SECTOR;
            if (base + SECTOR > u8.length) break;
            var sect = s;
            walkPack(u8, base, function (id, off, len, pts) {
                if (id >= 0xE0 && id <= 0xEF) {
                    v.hasVideo = true;
                    if (pts !== null) {
                        if (v.sPts === null) v.sPts = pts;
                        if (v.lastVideoPts === null || pts > v.lastVideoPts) v.lastVideoPts = pts;
                    }
                    if (full || v.pictures < 4) {
                        for (var p = off; p < off + len; p++) {
                            var b = u8[p];
                            if (carry0 === 0 && carry1 === 0 && carry2 === 1) {
                                if (b === 0x00) { v.pictures++; v.picSectors.push(sect); }
                                else if (b === 0xB7) v.hasSeqEnd = true;
                            }
                            carry0 = carry1; carry1 = carry2; carry2 = b;
                        }
                    }
                } else if (id === 0xBD && len > 0) {
                    var sid = u8[off];
                    if (v.audioFirst[sid] === undefined) v.audioFirst[sid] = sect;
                    if (v.audioSector < 0) { v.audioSub = sid; v.audioSector = sect; }
                }
                return true;
            });
            // Stop once the picture map and the first sector of every audio
            // stream are known — audio packs sit at the head of a VOBU.
            var haveAudio = Object.keys(v.audioFirst).length >= want;
            if (!full && v.sPts !== null && v.pictures >= 4 && haveAudio) break;
        }
        return v;
    }

    /* Splits a muxed program stream into VOBUs. Every VOBU begins with a NAV
       pack; ffmpeg's `-f dvd` muxer emits one per GOP. */
    function parseVob(u8, audioSubs) {
        var n = Math.floor(u8.length / SECTOR);
        var navs = [];
        for (var s = 0; s < n; s++) if (isNavPack(u8, s * SECTOR)) navs.push(s);
        if (!navs.length) {
            var e = new Error('dvd_no_nav_packs');
            e.code = 'DVD_NO_NAV';
            throw e;
        }
        var vobus = [];
        for (var i = 0; i < navs.length; i++) {
            var last = (i + 1 < navs.length ? navs[i + 1] : n) - 1;
            vobus.push(scanVobu(u8, navs[i], last, i === navs.length - 1, audioSubs));
        }
        // End times: a VOBU ends where the next one starts. The final VOBU gets
        // its span from its own picture count, which is why it is scanned fully.
        for (var k = 0; k < vobus.length; k++) {
            if (vobus[k].sPts === null) vobus[k].sPts = k ? vobus[k - 1].sPts : 0;
        }
        return { vobus: vobus, sectors: n };
    }

    // ── NAV pack generation ───────────────────────────────────────────────────
    /* Relative VOBU offset with the flag bits, exactly as dvdauthor getsect():
       bit31 = target holds video, bit30 = more than one video VOBU skipped.
       Jumps never leave the cell — that is what makes chapter boundaries hard
       stops for FF/REW instead of running into the next track. */
    function getsect(seg, cur, jump, skip, notfound) {
        var skipBit = 0;
        if (skip) {
            var lo = cur < jump ? cur + 1 : jump + 1;
            var hi = cur < jump ? jump - 1 : cur - 1;
            var found = false;
            for (var i = lo; i <= hi; i++) if (seg.vobus[i] && seg.vobus[i].hasVideo) { found = true; break; }
            skipBit = found ? 0 : 0x40000000;
        }
        if (jump < 0 || jump >= seg.vobus.length) return (notfound | skipBit) >>> 0;
        var d = Math.abs(seg.vobus[jump].sector - seg.vobus[cur].sector);
        return (d | (seg.vobus[jump].hasVideo ? 0x80000000 : 0) | skipBit) >>> 0;
    }

    /* Index of the VOBU covering `pts`, or -1 / length when outside the cell
       (dvdauthor returns l-1 / h+1 for the same reason: the caller then emits
       the end-of-cell marker). */
    function findVobuByPts(seg, pts) {
        var vs = seg.vobus, l = 0, h = vs.length - 1;
        if (h < l) return -1;
        if (pts < vs[l].sPts) return -1;
        if (pts >= vs[h].ePts) return vs.length;
        while (l < h) {
            var m = (l + h + 1) >> 1;
            if (pts < vs[m].sPts) h = m - 1; else l = m;
        }
        return l;
    }

    /* Rewrites the two private_stream_2 packets of every NAV pack in place. The
       pack header and system header (bytes 0x00..0x25) are ffmpeg's and are kept
       untouched — only the payloads we are responsible for get rebuilt. */
    function fillNavPacks(u8, seg, pal, audioSubs) {
        // audioSubs[i] is the sub-stream id of logical audio stream i; a_synca is
        // indexed by that logical number, so entry i points at the first packet
        // carrying audioSubs[i].
        var subs = (audioSubs && audioSubs.length) ? audioSubs : [-1];
        var vobus = seg.vobus;
        var vobFirstPts = vobus[0].sPts;
        var vobLastPts = vobus[vobus.length - 1].ePts;
        for (var i = 0; i < vobus.length; i++) {
            var v = vobus[i];
            var b = (v.sector - seg.sectorBase) * SECTOR;   // offset inside this segment's buffer

            var scr = readScr(u8, b + 4);
            // PCI + DSI headers, then a clean slate for both payloads.
            u8[b + 0x26] = 0; u8[b + 0x27] = 0; u8[b + 0x28] = 1; u8[b + 0x29] = 0xBF;
            be16(u8, b + 0x2a, 0x3d4); u8[b + 0x2c] = 0;
            u8.fill(0, b + 0x2d, b + 0x400);
            u8[b + 0x400] = 0; u8[b + 0x401] = 0; u8[b + 0x402] = 1; u8[b + 0x403] = 0xBF;
            be16(u8, b + 0x404, 0x3fa); u8[b + 0x406] = 1;
            u8.fill(0, b + 0x407, b + 0x800);

            var cellElapsed = bcdTime(v.sPts - vobus[0].sPts, pal);

            // ---- PCI ----
            be32(u8, b + 0x2d, v.sector);            // nv_pck_lbn
            // 0x35..0x38 prohibited user ops: none, so every remote key works.
            be32(u8, b + 0x39, v.sPts);              // vobu_s_ptm
            be32(u8, b + 0x3d, v.ePts);              // vobu_e_ptm
            if (v.hasSeqEnd) be32(u8, b + 0x41, v.ePts);   // vobu_se_e_ptm
            be32(u8, b + 0x45, cellElapsed);         // e_eltm

            // ---- DSI ----
            be32(u8, b + 0x407, scr);                // nv_pck_scr
            be32(u8, b + 0x40b, v.sector);           // nv_pck_lbn
            be32(u8, b + 0x40f, v.lastSector - v.sector);  // vobu_ea
            var refs = v.refEnds;
            for (var r = 0; r < 3; r++) be32(u8, b + 0x413 + r * 4, refs[Math.min(r, refs.length - 1)]);
            be16(u8, b + 0x41f, seg.vobId);          // vobu_vob_idn
            u8[b + 0x422] = seg.cellId;              // vobu_c_idn
            be32(u8, b + 0x423, cellElapsed);        // c_eltm
            be32(u8, b + 0x433, vobFirstPts);        // vob_v_s_s_ptm
            be32(u8, b + 0x437, vobLastPts);         // vob_v_e_e_ptm

            var nextVid = i + 1, prevVid = i - 1;
            while (nextVid < vobus.length && !vobus[nextVid].hasVideo) nextVid++;
            while (prevVid >= 0 && !vobus[prevVid].hasVideo) prevVid--;
            be32(u8, b + 0x4f1, getsect(seg, i, nextVid, 0, 0xbfffffff));   // next_video
            be32(u8, b + 0x541, getsect(seg, i, i + 1, 0, SRI_END_OF_CELL)); // next_vobu
            be32(u8, b + 0x545, getsect(seg, i, i - 1, 0, SRI_END_OF_CELL)); // prev_vobu
            be32(u8, b + 0x595, getsect(seg, i, prevVid, 0, 0xbfffffff));    // prev_video

            // FF/REW tables. Entries run in order of DECREASING time step, hence
            // the descending write offset for the forward table.
            var vff = i, vrew = i;
            for (var j = 0; j < 19; j++) {
                var nff = findVobuByPts(seg, v.sPts + TIMELINE[j] * HALFSEC);
                var nrew = findVobuByPts(seg, v.sPts - TIMELINE[j] * HALFSEC);
                be32(u8, b + 0x53d - j * 4, getsect(seg, i, nff, j >= 15 && nff > vff + 1, SRI_END_OF_CELL));
                be32(u8, b + 0x549 + j * 4, getsect(seg, i, nrew, j >= 15 && nrew < vrew - 1, SRI_END_OF_CELL));
                vff = nff; vrew = nrew;
            }

            // a_synca[0..7]: where each audio stream starts in this VOBU,
            // relative to the NAV pack. 0x3fff = this stream has no audio in this
            // VOBU; 0x0000 (left by the clear) = the stream slot does not exist.
            for (var as = 0; as < subs.length && as < 8; as++) {
                var sect = (v.audioFirst && v.audioFirst[subs[as]] !== undefined)
                    ? v.audioFirst[subs[as]]
                    : (subs[as] === -1 && v.audioSector >= 0 ? v.audioSector : -1);
                var a = 0x3fff;
                if (sect >= 0) {
                    var d = sect - v.sector;
                    a = d < 0 ? ((-d) | 0x8000) : d;
                    if (d > 0x1fff || d < -0x1fff) a = 0;
                }
                be16(u8, b + 0x599 + as * 2, a);
            }
            // sp_synca stays zero: no subpicture streams on this disc.
        }
    }

    /* ffmpeg's `-f dvd` writes the LPCM PES P-STD_buffer_size as 4096 B; a pressed
       DVD declares 10240 B, and a player allocates the LPCM decoder buffer to what
       the stream declares. 4096 B is smaller than the audio can drain across the
       worst inter-pack gap, so a strict player (a Samsung BD-C5500P, 2026-08)
       starves the LPCM buffer and adds a level-correlated buzz — while it plays
       commercial 10240 B LPCM discs cleanly. This rewrites the P-STD field of
       every LPCM (0xA0-0xA7) PES to 10240 B (scale 0, size 80 × 128), in place —
       2 bytes per packet, no offsets move, so NAV/DSI pointers are untouched.
       Independent of fillNavPacks (that rewrites private_stream_2). */
    function patchLpcmPstd(u8) {
        var n = Math.floor(u8.length / SECTOR), patched = 0;
        for (var s = 0; s < n; s++) {
            var b = s * SECTOR;
            if (u8[b] !== 0 || u8[b + 1] !== 0 || u8[b + 2] !== 1 || u8[b + 3] !== 0xBA) continue;
            var o = b + 14 + (u8[b + 13] & 7), limit = b + SECTOR;
            while (o + 9 <= limit) {
                if (u8[o] !== 0 || u8[o + 1] !== 0 || u8[o + 2] !== 1) break;
                var id = u8[o + 3], len = rd16(u8, o + 4);
                if (len <= 0 || o + 6 + len > limit) break;
                if (id === 0xBD) {
                    var f = u8[o + 7], hlen = u8[o + 8], sub = u8[o + 9 + hlen];
                    if ((sub & 0xF8) === 0xA0) {               // LPCM sub-stream
                        var p = o + 9, pd = (f >> 6) & 3;
                        if (pd === 2) p += 5; else if (pd === 3) p += 10;
                        if (f & 0x20) p += 6;                  // ESCR
                        if (f & 0x10) p += 3;                  // ES_rate
                        if (f & 0x08) p += 1;                  // DSM_trick_mode
                        if (f & 0x04) p += 1;                  // additional_copy_info
                        if (f & 0x02) p += 2;                  // PES_CRC
                        if (f & 0x01) {                        // PES_extension
                            var ef = u8[p++];
                            if (ef & 0x80) p += 16;            // PES_private_data
                            if (ef & 0x40) p += 1 + u8[p];     // pack_header_field
                            if (ef & 0x20) p += 2;             // program_packet_seq_counter
                            if (ef & 0x10) {                   // P-STD_buffer_flag → the field
                                u8[p] = 0x40;                  // '01' marker, scale 0, size hi bits 0
                                u8[p + 1] = 80;                // size 80 × 128 = 10240 B
                                patched++;
                            }
                        }
                    }
                }
                o += 6 + len;
            }
        }
        return patched;
    }

    /* ffmpeg's `-f dvd` writes THREE per-packet LPCM header fields as CONSTANTS
       that a pressed disc varies packet-by-packet: number_of_frames, the
       first_access_unit_pointer (byte offset to the first audio-frame boundary in
       this packet) and the audio_frame_number (a 0-19 counter). A strict player
       (Samsung BD-C5500P, 2026-08) uses these to align the fixed-size DVD audio
       frames while decoding; with ffmpeg's constants (7 / 0x0004 / 0x0c) it slices
       frames off their boundary, so the LPCM plays at HALF LEVEL and buzzes — while
       it plays commercial LPCM discs, and every other codec on our OWN disc
       (AC-3/DTS), cleanly. ffmpeg's own decoder and tolerant players read samples
       sequentially and ignore these fields, which is why a local decode is
       bit-perfect and the fault only shows on strict hardware.
       Reverse-engineered from a factory 48k/16 stereo LPCM disc that plays clean on
       that Samsung: the DVD audio frame is 80 samples (1/600 s at 48 kHz), so
       FRAME = 80 * channels * bytes-per-sample and, per packet,
         first_access_unit_pointer = ((FRAME - cumAudio % FRAME) % FRAME) + 4
         number_of_frames          = frame boundaries inside this packet's payload
         audio_frame_number        = running sum of number_of_frames, mod 20
       — verified to reproduce the factory values exactly. In place, 4 bytes per
       packet, no offsets move (NAV/DSI pointers untouched) — same contract as
       patchLpcmPstd. cumAudio counts LPCM audio-payload bytes from the first LPCM
       packet, which starts at frame 0, as the factory disc does. */
    function patchLpcmFrameHeader(u8) {
        var cumAudio = 0, fnum = 0, patched = 0;
        var n = Math.floor(u8.length / SECTOR);
        for (var s = 0; s < n; s++) {
            var b = s * SECTOR;
            if (u8[b] !== 0 || u8[b + 1] !== 0 || u8[b + 2] !== 1 || u8[b + 3] !== 0xBA) continue;
            var o = b + 14 + (u8[b + 13] & 7), limit = b + SECTOR;
            while (o + 9 <= limit) {
                if (u8[o] !== 0 || u8[o + 1] !== 0 || u8[o + 2] !== 1) break;
                var id = u8[o + 3], len = rd16(u8, o + 4);
                if (len <= 0 || o + 6 + len > limit) break;
                if (id === 0xBD) {
                    var hlen = u8[o + 8], ds = o + 9 + hlen, sub = u8[ds];
                    if ((sub & 0xF8) === 0xA0) {               // LPCM sub-stream
                        var fmt = u8[ds + 5], quant = (fmt >> 6) & 3, ch = (fmt & 7) + 1;
                        var bytesPer = quant === 2 ? 3 : quant === 1 ? 2.5 : 2;   // 24 / 20 / 16-bit
                        var frame = Math.round(80 * ch * bytesPer);              // 80 samples @48 kHz
                        var audioLen = len - 3 - hlen - 7;                       // payload after LPCM hdr
                        var pred = (frame - (cumAudio % frame)) % frame;
                        var fau = pred + 4;
                        var nfr = pred < audioLen ? Math.floor((audioLen - pred - 1) / frame) + 1 : 0;
                        u8[ds + 1] = nfr & 0xff;                // number_of_frames
                        u8[ds + 2] = (fau >> 8) & 0xff;         // first_access_unit_pointer (hi)
                        u8[ds + 3] = fau & 0xff;                // first_access_unit_pointer (lo)
                        u8[ds + 4] = fnum & 0xff;               // audio_frame_number
                        cumAudio += audioLen;
                        fnum = (fnum + nfr) % 20;
                        patched++;
                    }
                }
                o += 6 + len;
            }
        }
        return patched;
    }

    // ── IFO tables ────────────────────────────────────────────────────────────
    function sectorsFor(bytes) { return Math.ceil(bytes / SECTOR); }

    /* One audio-coding table: the VOB private_stream_1 sub-stream base per codec,
       and the VTS_AST_ATRT coding-mode code. A stream's real sub-stream id is
       base + its index AMONG streams of the same codec, which is exactly how the
       muxer numbers them (AC-3 #0 = 0x80, a 2nd AC-3 = 0x81, DTS #0 = 0x88 …). */
    var AUDIO_CODEC = {
        ac3:  { base: 0x80, mode: 0 },
        dts:  { base: 0x88, mode: 6 },
        lpcm: { base: 0xA0, mode: 4 }
    };
    /* Normalise opts.audioStreams (or the legacy single opts.audio) into the
       list the whole authoring path consumes. Order IS the logical stream order;
       stream 0 is the default. Each entry gains its sub-stream id. */
    function normAudioStreams(opts) {
        var list = opts.audioStreams;
        if (!list || !list.length) {
            var a = opts.audio || { lpcm: false, channels: 6, bits: 16, surround: true };
            list = [{ codec: a.lpcm ? 'lpcm' : 'ac3', channels: a.channels, bits: a.bits, surround: a.surround }];
        }
        var perCodec = {};
        return list.map(function (a) {
            var codec = AUDIO_CODEC[a.codec] ? a.codec : 'ac3';
            var idx = perCodec[codec] || 0; perCodec[codec] = idx + 1;
            return {
                codec: codec, sub: AUDIO_CODEC[codec].base + idx, mode: AUDIO_CODEC[codec].mode,
                lpcm: codec === 'lpcm', channels: a.channels || 6, bits: a.bits || 16,
                surround: a.surround !== false
            };
        });
    }

    function buildPttSrpt(nChapters) {
        var size = 12 + nChapters * 4;
        var b = new Uint8Array(sectorsFor(size) * SECTOR);
        be16(b, 0, 1);                  // one title
        be32(b, 4, size - 1);           // end address
        be32(b, 8, 12);                 // offset to this title's VTS_PTT list
        for (var i = 0; i < nChapters; i++) {
            b[12 + i * 4 + 1] = 1;      // PGCN — everything lives in PGC 1
            b[12 + i * 4 + 3] = i + 1;  // PGN  — program number = track number
        }
        return b;
    }

    function buildTtSrpt(nChapters, vtsStartSector) {
        var size = 8 + 12;
        var b = new Uint8Array(sectorsFor(size) * SECTOR);
        be16(b, 0, 1);
        be32(b, 4, size - 1);
        b[8] = 0x3c;                    // one sequential PGC, PTT + time search allowed
        b[9] = 1;                       // angles
        be16(b, 10, nChapters);
        b[14] = 1;                      // VTSN
        b[15] = 1;                      // VTS_TTN
        be32(b, 16, vtsStartSector);
        return b;
    }

    /* One 8-byte VTS_AST_ATRT audio attribute. Coding mode picks the base
       sub-stream range a player looks for; the low byte carries quantisation,
       48 kHz and the channel count. */
    function audioAttr(b, off, a) {
        b[off] = (a.mode << 5) | (a.surround ? 2 : 0);        // coding mode + application mode
        var quant = a.lpcm ? (a.bits === 24 ? 2 : a.bits === 20 ? 1 : 0) : 3; // 3 = DRC / N/A
        b[off + 1] = (quant << 6) | (0 << 4) | (a.channels - 1);              // 48 kHz + channels-1
        if (a.surround) b[off + 6] = 8;                       // suitable for Dolby Surround decoding
    }
    /* Video/audio attribute block, shared by VTSI_MAT 0x200 and the copy the VMG
       keeps in VMG_VTS_ATRT. `streams` are the logical audio streams in order. */
    function buildAvInfo(pal, wide, streams) {
        var b = new Uint8Array(0x300);
        var vattr = 0x4000                        // MPEG-2
                  | (pal ? 0x1000 : 0)            // PAL / NTSC
                  | (wide ? 0x0c00 : 0x0300);     // 16:9, or 4:3 with pan&scan+letterbox barred
        be16(b, 0, vattr);                        // picture_size 0 = 720x480/576
        var n = Math.min(streams.length, 8);
        be16(b, 2, n);                            // VTS_AST_Ns — number of audio streams
        for (var i = 0; i < n; i++) audioAttr(b, 4 + i * 8, streams[i]);
        b[0x55] = 0;                              // no subpicture streams
        return b;
    }

    function buildCellAdt(segments) {
        var size = 8 + segments.length * 12;
        var b = new Uint8Array(sectorsFor(size) * SECTOR);
        be16(b, 0, segments.length);              // number of VOBs
        be32(b, 4, size - 1);
        for (var i = 0; i < segments.length; i++) {
            var p = 8 + i * 12, s = segments[i];
            be16(b, p, s.vobId);
            b[p + 2] = s.cellId;
            be32(b, p + 4, s.vobus[0].sector);
            be32(b, p + 8, s.vobus[s.vobus.length - 1].lastSector);
        }
        return b;
    }

    function buildVobuAdmap(segments) {
        var nv = 0, i;
        for (i = 0; i < segments.length; i++) nv += segments[i].vobus.length;
        var size = 4 + nv * 4;
        var b = new Uint8Array(sectorsFor(size) * SECTOR);
        be32(b, 0, nv * 4 + 3);
        var p = 4;
        for (i = 0; i < segments.length; i++)
            for (var j = 0; j < segments[i].vobus.length; j++) {
                be32(b, p, segments[i].vobus[j].sector); p += 4;
            }
        return b;
    }

    /* Time map: sector to jump to for every N seconds of playback. Without it a
       player has no way to answer "skip to 12:30" other than guessing. */
    function buildTmapt(segments, pal) {
        var denom = pal ? 90000 : 90090;
        var total = 0, i;
        for (i = 0; i < segments.length; i++) total += segments[i].duration;
        var durSec = Math.floor((total - 1) / denom);
        var units = durSec ? Math.ceil(durSec / 2040) : 1;
        var nEntries = Math.floor(durSec / units);
        var size = 8 + 4 + 4 + nEntries * 4;
        var b = new Uint8Array(sectorsFor(size) * SECTOR);
        be16(b, 0, 1);                  // one PGC
        be32(b, 4, size - 1);
        be32(b, 8, 12);                 // offset to the map for PGC 1
        b[12] = units;
        be16(b, 14, nEntries);

        // Elapsed disc time -> (segment, VOBU). Cells are independently muxed so
        // each has its own timebase; walk the cumulative durations.
        function locate(t) {
            for (var s = 0; s < segments.length; s++) {
                var seg = segments[s];
                if (t < seg.duration) {
                    var idx = findVobuByPts(seg, t + seg.vobus[0].sPts);
                    if (idx < 0) idx = 0;
                    if (idx >= seg.vobus.length) idx = seg.vobus.length - 1;
                    return { seg: s, v: seg.vobus[idx] };
                }
                t -= seg.duration;
            }
            return null;
        }
        var cur = locate(units * denom);
        for (var j = 0; j < nEntries; j++) {
            var next = locate((j + 2) * units * denom);
            var p = 16 + j * 4;
            if (cur) {
                be32(b, p, cur.v.sector);
                if (!next || next.seg !== cur.seg) b[p] |= 0x80;   // next entry is in another cell
            }
            cur = next;
        }
        return b;
    }

    /* The PGC: one program (= one chapter) per track, one cell per program. */
    function buildPgc(segments, pal, streams) {
        var n = segments.length;
        var b = new Uint8Array(0xEC + 16 + n + 1 + n * 24 + n * 4 + 64);
        var total = 0, i;
        for (i = 0; i < n; i++) total += segments[i].duration;

        b[2] = n;                                  // programs
        b[3] = n;                                  // cells
        be32(b, 4, bcdTime(total, pal));           // total playback time
        // Audio stream control 0x0C..0x1B: 8 × 2 bytes. Entry i (logical stream
        // i) = available(bit7) | its sub-stream number (low 3 bits). Stream 0 is
        // the one the player selects on insert.
        var na = Math.min(streams.length, 8);
        for (i = 0; i < na; i++) b[12 + i * 2] = 0x80 | (streams[i].sub & 7);
        b[163] = 0;                                // no still time
        for (i = 0; i < 16; i++) be32(b, 164 + i * 4, 0x108080);   // neutral CLUT

        // Command table: no pre commands, a single post command that exits the
        // PGC when the last track finishes (otherwise the player hangs on the
        // final still frame).
        var d = 0xEC;
        be16(b, 228, d);
        be16(b, d, 0);        // pre commands
        be16(b, d + 2, 1);    // post commands
        be16(b, d + 4, 0);    // cell commands
        be16(b, d + 6, 7 + 8);
        b[d + 8] = 0x30; b[d + 9] = 0x01;          // Exit
        d += 16;

        be16(b, 230, d);                            // program map
        for (i = 0; i < n; i++) b[d++] = i + 1;     // program k starts at cell k
        d += d & 1;

        be16(b, 232, d);                            // cell playback information
        for (i = 0; i < n; i++) {
            var s = segments[i];
            b[d] = 0x02;                            // STC discontinuity: each cell has its own timebase
            be32(b, d + 4, bcdTime(s.duration, pal));
            be32(b, d + 8, s.vobus[0].sector);
            be32(b, d + 16, s.vobus[s.vobus.length - 1].sector);
            be32(b, d + 20, s.vobus[s.vobus.length - 1].lastSector);
            d += 24;
        }

        be16(b, 234, d);                            // cell position information
        for (i = 0; i < n; i++) {
            be16(b, d, segments[i].vobId);
            b[d + 3] = segments[i].cellId;
            d += 4;
        }
        return b.subarray(0, d);
    }

    function buildPgcit(segments, pal, streams) {
        var pgc = buildPgc(segments, pal, streams);
        var size = 16 + pgc.length;
        var b = new Uint8Array(sectorsFor(size) * SECTOR);
        be16(b, 0, 1);                  // one PGC
        be32(b, 4, size - 1);
        b[8] = 0x81;                    // title 1
        be32(b, 12, 16);                // offset to the PGC
        b.set(pgc, 16);
        return b;
    }

    // ── whole-disc assembly ───────────────────────────────────────────────────
    /*
     * opts = {
     *   segments: [{ vob: Uint8Array, title: string }],   one muxed VOB per track
     *   pal: bool, wide: bool,
     *   audio: { lpcm, channels, bits, surround },              one stream (legacy)
     *   audioStreams: [{ codec:'ac3'|'dts'|'lpcm', channels, bits, surround }],  in
     *                 logical order — stream 0 is the default the player picks.
     *   volumeId: string
     * }
     * Returns { files: { name: Uint8Array }, stats }.
     */
    function authorDisc(opts) {
        var pal = !!opts.pal;
        // Audio: either the legacy single descriptor (opts.audio) or a list of
        // parallel streams (opts.audioStreams), the first being the default the
        // player picks on insert. The single form is wrapped so one code path
        // serves both — a one-stream title stays byte-for-byte what it was.
        var streams = normAudioStreams(opts);
        var audioSubs = streams.map(function (a) { return a.sub; });
        var srcs = opts.segments || [];
        if (!srcs.length) throw new Error('dvd_no_segments');

        // 1. Parse each muxed segment and place it in the title-set sector space.
        var segments = [], base = 0, i;
        for (i = 0; i < srcs.length; i++) {
            var parsed = parseVob(srcs[i].vob, audioSubs);
            var seg = {
                buf: srcs[i].vob, title: srcs[i].title || '',
                vobId: i + 1, cellId: 1,
                sectorBase: base, vobus: parsed.vobus, sectors: parsed.sectors
            };
            for (var k = 0; k < seg.vobus.length; k++) {
                seg.vobus[k].sector += base;
                seg.vobus[k].lastSector += base;
            }
            // End times, now that neighbours are known.
            var ft = frameTicks(pal);
            for (k = 0; k < seg.vobus.length; k++) {
                var v = seg.vobus[k];
                v.ePts = (k + 1 < seg.vobus.length) ? seg.vobus[k + 1].sPts
                                                    : v.sPts + Math.max(1, v.pictures) * ft;
                // Reference-frame end sectors, relative to the VOBU start.
                v.refEnds = [];
                for (var r = 1; r <= 3 && r < v.picSectors.length; r++)
                    v.refEnds.push(v.picSectors[r] - v.sector);
                if (!v.refEnds.length) v.refEnds.push(Math.max(0, v.picSectors.length ? v.picSectors[0] - v.sector : 0));
            }
            seg.duration = seg.vobus[seg.vobus.length - 1].ePts - seg.vobus[0].sPts;
            base += parsed.sectors;
            segments.push(seg);
        }
        var vobSectors = base;

        // 2. Fill every NAV pack.
        for (i = 0; i < segments.length; i++) fillNavPacks(segments[i].buf, segments[i], pal, audioSubs);

        // 2b. Raise the LPCM P-STD_buffer_size from ffmpeg's 4096 B to the
        // pressed-disc 10240 B so a strict player does not starve the LPCM buffer.
        for (i = 0; i < segments.length; i++) patchLpcmPstd(segments[i].buf);

        // 2c. Rewrite the per-packet LPCM frame headers (number_of_frames,
        // first_access_unit_pointer, audio_frame_number) that ffmpeg emits as
        // constants — a strict player mis-aligns the audio frames otherwise and the
        // PCM track plays at half level with a buzz (Samsung BD-C5500P, 2026-08).
        for (i = 0; i < segments.length; i++) patchLpcmFrameHeader(segments[i].buf);

        // 3. VTS IFO. Table order is fixed by the spec's sector pointers.
        var pttSrpt = buildPttSrpt(segments.length);
        var pgcit = buildPgcit(segments, pal, streams);
        var tmapt = buildTmapt(segments, pal);
        var cAdt = buildCellAdt(segments);
        var vobuAdmap = buildVobuAdmap(segments);

        var used = 1 + pttSrpt.length / SECTOR + pgcit.length / SECTOR + tmapt.length / SECTOR +
                   cAdt.length / SECTOR + vobuAdmap.length / SECTOR;
        var ifoSectors = Math.max(IFO_MIN_SECTORS, used);
        var vtsIfo = new Uint8Array(ifoSectors * SECTOR);
        var m = vtsIfo;
        var ascii = 'DVDVIDEO-VTS';
        for (i = 0; i < ascii.length; i++) m[i] = ascii.charCodeAt(i);
        be32(m, 0x0c, ifoSectors + vobSectors + ifoSectors - 1);  // last sector of the title set (= end of BUP)
        be32(m, 0x1c, ifoSectors - 1);                            // last sector of this IFO
        m[0x21] = 0x11;                                           // spec version 1.1
        be32(m, 0x80, 0x7ff);                                     // last byte of VTSI_MAT
        be32(m, 0xc4, ifoSectors);                                // VTSTT_VOBS starts right after the IFO
        var sect = 1;
        be32(m, 0xc8, sect); m.set(pttSrpt, sect * SECTOR); sect += pttSrpt.length / SECTOR;
        be32(m, 0xcc, sect); m.set(pgcit, sect * SECTOR); sect += pgcit.length / SECTOR;
        be32(m, 0xd4, sect); m.set(tmapt, sect * SECTOR); sect += tmapt.length / SECTOR;
        be32(m, 0xe0, sect); m.set(cAdt, sect * SECTOR); sect += cAdt.length / SECTOR;
        be32(m, 0xe4, sect); m.set(vobuAdmap, sect * SECTOR);
        var avInfo = buildAvInfo(pal, !!opts.wide, streams);
        m.set(avInfo, 0x200);

        // 4. VMG IFO: no menus, so First Play simply jumps straight to title 1.
        var ttSrpt = buildTtSrpt(segments.length, IFO_MIN_SECTORS * 2);
        var vtsAtrtSize = 8 + 4 + 0x308;
        var vmgUsed = 1 + ttSrpt.length / SECTOR + sectorsFor(vtsAtrtSize);
        var vmgSectors = Math.max(IFO_MIN_SECTORS, vmgUsed);
        var vmgIfo = new Uint8Array(vmgSectors * SECTOR);
        var g = vmgIfo;
        ascii = 'DVDVIDEO-VMG';
        for (i = 0; i < ascii.length; i++) g[i] = ascii.charCodeAt(i);
        be32(g, 0x0c, vmgSectors * 2 - 1);       // last sector of the VMG set (IFO + BUP)
        be32(g, 0x1c, vmgSectors - 1);
        g[0x21] = 0x11;
        g[0x27] = 1;                              // one volume
        g[0x29] = 1;                              // volume 1
        g[0x2a] = 1;                              // side 1
        be16(g, 0x3e, 1);                         // one title set
        var provider = (opts.provider || 'TIMBRICA').slice(0, 31);
        for (i = 0; i < provider.length; i++) g[0x40 + i] = provider.charCodeAt(i) & 127;
        be32(g, 0x84, 0x400);                     // First Play PGC lives at byte 0x400
        be32(g, 0xc4, 1);                         // TT_SRPT
        be32(g, 0xd0, 1 + ttSrpt.length / SECTOR); // VMG_VTS_ATRT
        g.set(ttSrpt, SECTOR);

        // First Play PGC — a bare "PlayTitle 1".
        var fp = 0x400;
        g[fp + 7] = (pal ? 1 : 3) << 6;           // playback time: frame-rate code only
        g[fp + 0xe5] = 0xec;                      // offset to the command table
        be16(g, fp + 0xec, 1);                    // one pre command
        be16(g, fp + 0xec + 6, 7 + 8);
        // JumpTT 1 — the title number lives in byte 5 of the 8-byte VM command.
        g[fp + 0xf4] = 0x30; g[fp + 0xf5] = 0x02; g[fp + 0xf9] = 0x01;
        be32(g, 0x80, 0x4ec + 7 + 8);             // last byte of VMGI_MAT

        // VMG_VTS_ATRT keeps a copy of the title set's attributes.
        var atrt = new Uint8Array(sectorsFor(vtsAtrtSize) * SECTOR);
        be16(atrt, 0, 1);
        be32(atrt, 4, 0x30c + 8 - 1);
        be32(atrt, 8, 12);
        be32(atrt, 12, 0x307);
        atrt.set(vtsIfo.subarray(0x22, 0x26), 16);      // VTS_CAT
        atrt.set(vtsIfo.subarray(0x100, 0x400), 20);    // attribute block
        g.set(atrt, (1 + ttSrpt.length / SECTOR) * SECTOR);

        // 5. Files. The VOB set is split at the 1 GiB spec limit, on VOBU
        //    boundaries so no VOBU ever straddles two files.
        var files = {};
        files['VIDEO_TS.IFO'] = vmgIfo;
        files['VIDEO_TS.BUP'] = vmgIfo;
        files['VTS_01_0.IFO'] = vtsIfo;
        files['VTS_01_0.BUP'] = vtsIfo;

        var parts = splitVobs(segments, vobSectors);
        for (i = 0; i < parts.length; i++) files['VTS_01_' + (i + 1) + '.VOB'] = parts[i];

        return {
            files: files,
            stats: {
                tracks: segments.length,
                vobSectors: vobSectors,
                ifoSectors: ifoSectors,
                totalSectors: vmgSectors * 2 + ifoSectors * 2 + vobSectors,
                totalBytes: (vmgSectors * 2 + ifoSectors * 2 + vobSectors) * SECTOR,
                durationSec: segments.reduce(function (a, s) { return a + s.duration; }, 0) / (pal ? 90000 : 90090),
                vobus: segments.reduce(function (a, s) { return a + s.vobus.length; }, 0),
                chapters: segments.map(function (s, ix) {
                    return { n: ix + 1, title: s.title, sector: s.vobus[0].sector, sec: s.duration / (pal ? 90000 : 90090) };
                })
            }
        };
    }

    /* Concatenates the per-track streams into VTS_01_n.VOB parts, breaking only
       at VOBU boundaries and never past 1 GiB. */
    function splitVobs(segments, totalSectors) {
        var parts = [], cur = [], curSectors = 0, i;
        function flush() {
            if (!cur.length) return;
            var out = new Uint8Array(curSectors * SECTOR), off = 0;
            for (var k = 0; k < cur.length; k++) { out.set(cur[k], off); off += cur[k].length; }
            parts.push(out); cur = []; curSectors = 0;
        }
        for (i = 0; i < segments.length; i++) {
            var seg = segments[i];
            if (curSectors && curSectors + seg.sectors > VOB_MAX_SECTORS) flush();
            if (seg.sectors > VOB_MAX_SECTORS) {
                // A single track bigger than 1 GiB: cut it at the VOBU boundary
                // closest to the limit.
                var startIdx = 0;
                while (startIdx < seg.vobus.length) {
                    var endIdx = startIdx, lastOk = startIdx;
                    var baseSect = seg.vobus[startIdx].sector - seg.sectorBase;
                    while (endIdx < seg.vobus.length &&
                           (seg.vobus[endIdx].lastSector - seg.sectorBase) - baseSect + 1 <= VOB_MAX_SECTORS) {
                        lastOk = endIdx; endIdx++;
                    }
                    var from = baseSect * SECTOR;
                    var to = ((seg.vobus[lastOk].lastSector - seg.sectorBase) + 1) * SECTOR;
                    flush();
                    parts.push(seg.buf.subarray(from, to));
                    startIdx = lastOk + 1;
                }
                continue;
            }
            cur.push(seg.buf); curSectors += seg.sectors;
        }
        flush();
        return parts;
    }

    // ── ZIP packaging ─────────────────────────────────────────────────────────
    /* A DVD image is hundreds of MB of already-incompressible MPEG, so the ZIP
       is STORE-only: no deflate pass, and the payload is handed to Blob() by
       reference instead of being copied into one giant buffer — which is the
       difference between working and an out-of-memory tab on a 2-hour disc.
       (JSZip would deflate-attempt and materialise the whole archive first.) */
    var CRC_TABLE = null;
    function crc32(u8, seed) {
        if (!CRC_TABLE) {
            CRC_TABLE = new Int32Array(256);
            for (var n = 0; n < 256; n++) {
                var c = n;
                for (var k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
                CRC_TABLE[n] = c;
            }
        }
        var crc = (seed === undefined ? 0xFFFFFFFF : seed);
        for (var i = 0; i < u8.length; i++) crc = CRC_TABLE[(crc ^ u8[i]) & 255] ^ (crc >>> 8);
        return crc;
    }

    function zipStore(files, prefix, onProgress) {
        var names = Object.keys(files);
        var parts = [], central = [], offset = 0;
        var enc = (typeof TextEncoder !== 'undefined') ? new TextEncoder() : null;
        function nameBytes(s) {
            if (enc) return enc.encode(s);
            var a = new Uint8Array(s.length);
            for (var i = 0; i < s.length; i++) a[i] = s.charCodeAt(i) & 255;
            return a;
        }
        for (var i = 0; i < names.length; i++) {
            var data = files[names[i]];
            /* This writer reads `data.length` and `data[i]`, so a Blob or a File
               is not an input it can describe: `.length` is undefined, every size
               field becomes 0 and the CRC becomes 0 — and the produced archive is
               the worst possible shape, because the member BYTES are still there
               (Blob parts carry them) while every header says the entry is empty.
               An extractor believes the headers, so the user gets the right file
               names with nothing inside, and the download, the size on disk and
               every counter look healthy. Measured 2026-09-12 on the album FLAC
               lane of /surround-disc: a 328 KB archive, four entries, 0 bytes each.
               Refusing here is what makes that silent; zipStoreAsync below is the
               entry point that accepts Blobs. */
            if (!data || typeof data.length !== 'number') {
                throw new TypeError('zipStore needs byte arrays; use zipStoreAsync for Blob/File entries (' + names[i] + ')');
            }
            var nb = nameBytes((prefix || '') + names[i]);
            // A pre-measured member (from zipStoreAsync) brings its own CRC and the
            // Blob that holds the bytes; a plain byte array is measured here.
            var body = data.__blob || data;
            var crc = (data.__crc !== undefined) ? data.__crc : ((crc32(data) ^ 0xFFFFFFFF) >>> 0);
            var local = new Uint8Array(30 + nb.length), dv = new DataView(local.buffer);
            dv.setUint32(0, 0x04034b50, true);
            dv.setUint16(4, 20, true);          // version needed
            dv.setUint16(8, 0, true);           // method 0 = stored
            dv.setUint32(14, crc, true);
            dv.setUint32(18, data.length, true);
            dv.setUint32(22, data.length, true);
            dv.setUint16(26, nb.length, true);
            local.set(nb, 30);
            parts.push(local, body);

            var cen = new Uint8Array(46 + nb.length), cv = new DataView(cen.buffer);
            cv.setUint32(0, 0x02014b50, true);
            cv.setUint16(4, 20, true); cv.setUint16(6, 20, true);
            cv.setUint16(10, 0, true);
            cv.setUint32(16, crc, true);
            cv.setUint32(20, data.length, true);
            cv.setUint32(24, data.length, true);
            cv.setUint16(28, nb.length, true);
            cv.setUint32(42, offset, true);
            cen.set(nb, 46);
            central.push(cen);
            offset += local.length + data.length;
            if (onProgress) onProgress((i + 1) / names.length);
        }
        var cenSize = 0;
        for (i = 0; i < central.length; i++) cenSize += central[i].length;
        var end = new Uint8Array(22), ev = new DataView(end.buffer);
        ev.setUint32(0, 0x06054b50, true);
        ev.setUint16(8, central.length, true);
        ev.setUint16(10, central.length, true);
        ev.setUint32(12, cenSize, true);
        ev.setUint32(16, offset, true);
        return new Blob(parts.concat(central, [end]), { type: 'application/zip' });
    }

    /* Same archive, for callers whose members are Blobs or Files (an encoder's
       output, a picked file). A Blob cannot be measured synchronously, so the CRC
       is accumulated over slices — 8 MiB at a time, which keeps a gigabyte album
       out of memory while still producing the real CRC and size that zipStore
       demands. Byte-array members pass straight through, so a mixed map is fine.
       The output is assembled from the ORIGINAL Blobs, not from the slices: the
       bytes are still referenced rather than copied. */
    var ZIP_CRC_CHUNK = 8 * 1024 * 1024;
    async function zipStoreAsync(files, prefix, onProgress) {
        var names = Object.keys(files), measured = {};
        for (var i = 0; i < names.length; i++) {
            var data = files[names[i]];
            if (data && typeof data.length === 'number') { measured[names[i]] = data; continue; }
            if (!data || typeof data.size !== 'number' || typeof data.slice !== 'function') {
                throw new TypeError('zipStoreAsync needs a byte array or a Blob (' + names[i] + ')');
            }
            var run;
            for (var at = 0; at < data.size; at += ZIP_CRC_CHUNK) {
                var part = new Uint8Array(await data.slice(at, Math.min(at + ZIP_CRC_CHUNK, data.size)).arrayBuffer());
                run = crc32(part, run);
            }
            if (run === undefined) run = crc32(new Uint8Array(0));   // an empty member
            // A shim the writer CAN measure, still backed by the original Blob.
            measured[names[i]] = { length: data.size, __crc: (run ^ 0xFFFFFFFF) >>> 0, __blob: data };
        }
        return zipStore(measured, prefix, onProgress);
    }

    /* Count the NAV packs across a whole VOB set (VTS_01_1.VOB … _9.VOB) using
       the strict isNavPack the parser trusts, and confirm each one's DSI LBN
       matches its absolute sector. Consumers verifying a disc must use THIS, not
       a per-file scan — a title over 1 GiB spans several VOB files, and a scan of
       only the first file under-counts and cries "structure broken" on a disc
       that is actually fine (the field report that prompted it). `files` is the
       authored file map; returns { navs, bad } over the ordered VOB set. */
    function countNavPacks(files) {
        var names = Object.keys(files).filter(function (n) { return /^VTS_01_[1-9]\d*\.VOB$/.test(n); })
            .sort(function (a, b) {
                return (+a.match(/_(\d+)\.VOB$/)[1]) - (+b.match(/_(\d+)\.VOB$/)[1]);
            });
        var navs = 0, bad = 0, base = 0;
        for (var f = 0; f < names.length; f++) {
            var vob = files[names[f]];
            var n = Math.floor(vob.length / SECTOR);
            for (var s = 0; s < n; s++) {
                if (!isNavPack(vob, s * SECTOR)) continue;
                navs++;
                var o = s * SECTOR, sum = 0;
                for (var k = 0x2d; k < 0x60; k++) sum += vob[o + k];
                var lbn = (vob[o + 0x40b] * 16777216) + (vob[o + 0x40c] << 16) + (vob[o + 0x40d] << 8) + vob[o + 0x40e];
                if (!sum || lbn !== base + s) bad++;
            }
            base += n;
        }
        return { navs: navs, bad: bad };
    }

    return {
        SECTOR: SECTOR,
        parseVob: parseVob,
        authorDisc: authorDisc,
        countNavPacks: countNavPacks,
        isNavPack: isNavPack,
        zipStore: zipStore,
        zipStoreAsync: zipStoreAsync,
        crc32: crc32,
        bcdTime: bcdTime,
        readScr: readScr,
        readPts: readPts,
        _internals: { getsect: getsect, findVobuByPts: findVobuByPts, buildAvInfo: buildAvInfo }
    };
}));
